(function () {
  'use strict';

  var instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function hydrateVideoSources(video) {
    if (!video || video.dataset.ugcHydrated === 'true') return false;
    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return false;
    sources.forEach(function (source) {
      var src = source.getAttribute('data-src');
      if (src) {
        source.setAttribute('src', src);
        source.removeAttribute('data-src');
      }
    });
    video.dataset.ugcHydrated = 'true';
    try {
      video.load();
    } catch (e) {
      /* ignore */
    }
    return true;
  }

  function safePlay(video) {
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  }

  function UgcInstagram(root) {
    this.root = root;
    this.track = root.querySelector('[data-ugc-track]');
    this.prevBtn = root.querySelector('[data-ugc-prev]');
    this.nextBtn = root.querySelector('[data-ugc-next]');
    this.tiles = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-open]'));
    this.modals = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-modal]'));
    this.flkty = null;
    this.activeModal = null;
    this.resizeTimer = null;
    this.ready = false;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.scheduleInit();
  }

  UgcInstagram.prototype.scheduleInit = function () {
    var _this = this;
    if (!('IntersectionObserver' in window)) {
      this.init();
      return;
    }

    var boot = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          boot.disconnect();
          _this.init();
        });
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );
    boot.observe(this.root);
  };

  UgcInstagram.prototype.init = function () {
    if (this.ready) return;
    this.ready = true;
    this.initCarousel();
    this.bindTiles();
    this.bindModals();
    this.observeVideos();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  };

  UgcInstagram.prototype.onResize = function () {
    var _this = this;
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(function () {
      _this.refreshCarousel();
    }, 120);
  };

  UgcInstagram.prototype.refreshCarousel = function () {
    if (!this.flkty) return;
    this.flkty.resize();
    this.flkty.reposition();
    this.updateNav();
    this.playVisibleVideos();
  };

  UgcInstagram.prototype.initCarousel = function () {
    if (!this.track || typeof Flickity === 'undefined') return;
    if (this.track.classList.contains('flickity-enabled')) return;

    var slideCount = this.track.querySelectorAll('.ugc-instagram__slide').length;
    if (slideCount < 1) return;

    var _this = this;
    this.flkty = new Flickity(this.track, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: false,
      cellSelector: '.ugc-instagram__slide',
      dragThreshold: 10,
      // Física más firme: evita el “rebote” al pulsar flechas (overshoot al asentar)
      selectedAttraction: 0.2,
      friction: 0.8,
      freeScroll: false,
      freeScrollFriction: 0.07,
      groupCells: false,
      lazyLoad: false
    });

    this.updateNav();
    this.flkty.on('select', function () {
      _this.updateNav();
    });
    this.flkty.on('settle', function () {
      _this.playVisibleVideos();
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        _this.flkty.previous();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        _this.flkty.next();
      });
    }

    requestAnimationFrame(function () {
      _this.refreshCarousel();
    });
  };

  UgcInstagram.prototype.updateNav = function () {
    if (!this.flkty) return;
    var atStart = this.flkty.selectedIndex === 0;
    var atEnd = this.flkty.selectedIndex === this.flkty.slides.length - 1;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  };

  UgcInstagram.prototype.getVisibleCarouselVideos = function () {
    if (!this.track) return [];
    var videos = Array.prototype.slice.call(this.track.querySelectorAll('[data-ugc-lazy-video]'));
    if (!videos.length) return [];

    var rootRect = this.track.getBoundingClientRect();
    return videos.filter(function (video) {
      var rect = video.getBoundingClientRect();
      var horizontallyVisible = rect.right > rootRect.left + 8 && rect.left < rootRect.right - 8;
      var verticallyVisible = rect.bottom > 0 && rect.top < (window.innerHeight || 0);
      return horizontallyVisible && verticallyVisible;
    });
  };

  UgcInstagram.prototype.playVisibleVideos = function () {
    if (!this.track || prefersReducedMotion()) return;

    var visible = this.getVisibleCarouselVideos();
    var visibleSet = new Set(visible);
    var all = this.track.querySelectorAll('[data-ugc-lazy-video]');

    all.forEach(function (video) {
      if (visibleSet.has(video)) {
        hydrateVideoSources(video);
        safePlay(video);
      } else if (!video.paused) {
        video.pause();
      }
    });
  };

  UgcInstagram.prototype.observeVideos = function () {
    var _this = this;
    if (!this.track) return;

    if (!('IntersectionObserver' in window)) {
      this.playVisibleVideos();
      return;
    }

    var videos = this.track.querySelectorAll('[data-ugc-lazy-video]');
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            hydrateVideoSources(video);
            if (!prefersReducedMotion()) safePlay(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { root: null, rootMargin: '80px 0px', threshold: 0.2 }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
    this.videoObserver = observer;
    this.playVisibleVideos();
  };

  UgcInstagram.prototype.bindTiles = function () {
    var _this = this;
    this.tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        var id = tile.getAttribute('data-ugc-open');
        if (id) _this.openModal(id);
      });
    });
  };

  UgcInstagram.prototype.bindModals = function () {
    var _this = this;
    this.modals.forEach(function (modal) {
      var closeEls = modal.querySelectorAll('[data-ugc-close]');
      closeEls.forEach(function (el) {
        el.addEventListener('click', function () {
          _this.closeModal();
        });
      });

      var toggle = modal.querySelector('[data-ugc-caption-toggle]');
      var caption = modal.querySelector('[data-ugc-caption]');
      if (toggle && caption) {
        requestAnimationFrame(function () {
          if (caption.scrollHeight > caption.clientHeight + 4) {
            toggle.hidden = false;
          }
        });
        toggle.addEventListener('click', function () {
          var expanded = caption.classList.toggle('is-collapsed') === false;
          toggle.classList.toggle('is-expanded', expanded);
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
      }

      _this.bindSwipeDismiss(modal);
    });
  };

  UgcInstagram.prototype.bindSwipeDismiss = function (modal) {
    var _this = this;
    var dialog = modal.querySelector('[data-ugc-dialog]');
    if (!dialog) return;

    var startY = 0;
    var startX = 0;
    var currentY = 0;
    var dragging = false;
    var canDismiss = false;

    function isMobileShopScroll() {
      return (
        dialog.hasAttribute('data-ugc-has-shop') &&
        window.matchMedia('(max-width: 767px)').matches
      );
    }

    function getScrollTop() {
      if (isMobileShopScroll()) {
        return dialog.scrollTop || 0;
      }
      var body = modal.querySelector('[data-ugc-modal-body]');
      return body ? body.scrollTop : 0;
    }

    function resetDrag() {
      dragging = false;
      canDismiss = false;
      dialog.classList.remove('is-dragging');
      dialog.style.transform = '';
      dialog.style.opacity = '';
    }

    dialog.addEventListener(
      'touchstart',
      function (event) {
        if (!modal.classList.contains('is-open')) return;
        if (event.touches.length !== 1) return;
        startY = event.touches[0].clientY;
        startX = event.touches[0].clientX;
        currentY = startY;
        canDismiss = getScrollTop() <= 2;
        dragging = false;
      },
      { passive: true }
    );

    dialog.addEventListener(
      'touchmove',
      function (event) {
        if (!modal.classList.contains('is-open')) return;
        if (event.touches.length !== 1) return;

        currentY = event.touches[0].clientY;
        var deltaY = currentY - startY;
        var deltaX = Math.abs(event.touches[0].clientX - startX);

        if (!dragging) {
          if (!canDismiss || deltaY < 8 || deltaX > deltaY) return;
          dragging = true;
          dialog.classList.add('is-dragging');
        }

        if (!dragging) return;

        var translate = Math.max(0, deltaY);
        dialog.style.transform = 'translateY(' + translate + 'px)';
        dialog.style.opacity = String(Math.max(0.45, 1 - translate / 420));
      },
      { passive: true }
    );

    dialog.addEventListener(
      'touchend',
      function () {
        if (!dragging) {
          resetDrag();
          return;
        }

        var deltaY = currentY - startY;
        if (deltaY > 110) {
          _this.closeModal();
        }
        resetDrag();
      },
      { passive: true }
    );

    dialog.addEventListener(
      'touchcancel',
      function () {
        resetDrag();
      },
      { passive: true }
    );
  };

  UgcInstagram.prototype.openModal = function (id) {
    var modal = this.root.querySelector('[data-ugc-modal="' + id + '"]');
    if (!modal) return;

    this.closeModal(true);
    this.activeModal = modal;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ugc-instagram-lock');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeyDown);

    var body = modal.querySelector('[data-ugc-modal-body]');
    if (body) body.scrollTop = 0;

    var dialog = modal.querySelector('[data-ugc-dialog]');
    if (dialog) {
      dialog.scrollTop = 0;
      dialog.style.transform = '';
      dialog.style.opacity = '';
      dialog.classList.remove('is-dragging');
    }

    var video = modal.querySelector('[data-ugc-modal-video], video');
    if (video) {
      hydrateVideoSources(video);
      this.syncMediaAspect(modal, video);
      video.currentTime = 0;
      safePlay(video);
    } else {
      var img = modal.querySelector('.ugc-instagram__modal-media img');
      if (img) this.syncMediaAspect(modal, img);
    }

    var closeBtn = modal.querySelector('[data-ugc-close-btn]');
    if (closeBtn) closeBtn.focus();
  };

  UgcInstagram.prototype.syncMediaAspect = function (modal, mediaEl) {
    var dialog = modal.querySelector('[data-ugc-dialog]');
    if (!dialog || !mediaEl) return;

    var applyRatio = function (ratio) {
      if (!ratio || !isFinite(ratio) || ratio <= 0) return;
      dialog.style.setProperty('--ugc-media-ratio', String(ratio));
      dialog.classList.remove(
        'ugc-instagram__modal-dialog--portrait',
        'ugc-instagram__modal-dialog--square',
        'ugc-instagram__modal-dialog--landscape'
      );
      var orientation = 'square';
      if (ratio < 0.95) orientation = 'portrait';
      else if (ratio > 1.15) orientation = 'landscape';
      dialog.classList.add('ugc-instagram__modal-dialog--' + orientation);
    };

    if (mediaEl.tagName === 'VIDEO') {
      var fromVideo = function () {
        if (mediaEl.videoWidth > 0 && mediaEl.videoHeight > 0) {
          applyRatio(mediaEl.videoWidth / mediaEl.videoHeight);
        }
      };
      if (mediaEl.readyState >= 1) fromVideo();
      else mediaEl.addEventListener('loadedmetadata', fromVideo, { once: true });
      return;
    }

    var fromImage = function () {
      if (mediaEl.naturalWidth > 0 && mediaEl.naturalHeight > 0) {
        applyRatio(mediaEl.naturalWidth / mediaEl.naturalHeight);
      }
    };
    if (mediaEl.complete) fromImage();
    else mediaEl.addEventListener('load', fromImage, { once: true });
  };

  UgcInstagram.prototype.closeModal = function (silent) {
    if (!this.activeModal) return;
    var modal = this.activeModal;
    var video = modal.querySelector('[data-ugc-modal-video], video');
    if (video) video.pause();

    var dialog = modal.querySelector('[data-ugc-dialog]');
    if (dialog) {
      dialog.style.transform = '';
      dialog.style.opacity = '';
      dialog.classList.remove('is-dragging');
    }

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    this.activeModal = null;

    if (!silent) {
      document.documentElement.classList.remove('ugc-instagram-lock');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeyDown);
    }
  };

  UgcInstagram.prototype.onKeyDown = function (event) {
    if (event.key === 'Escape') this.closeModal();
  };

  UgcInstagram.prototype.destroy = function () {
    this.closeModal();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.clearTimeout(this.resizeTimer);
    if (this.videoObserver) this.videoObserver.disconnect();
    if (this.flkty) {
      this.flkty.destroy();
      this.flkty = null;
    }
  };

  function mount(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new UgcInstagram(root));
  }

  function unmount(root) {
    if (!root || !instances.has(root)) return;
    instances.get(root).destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-ugc-instagram]');
    roots.forEach(mount);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-ugc-instagram]');
    if (root) unmount(root);
  });

  if (document.readyState !== 'loading') {
    initAll(document);
  }
})();
